import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LuPlus, LuTrash2 } from "react-icons/lu";
import {
  createExam,
  listExamsAdmin,
  deleteExam,
} from "../../Services/phase2Service";

const blankQuestion = () => ({
  question: "",
  options: [
    { key: "a", text: "" },
    { key: "b", text: "" },
    { key: "c", text: "" },
    { key: "d", text: "" },
  ],
  correct_option: "a",
  marks: 1,
});

const CertificationAdmin = () => {
  const [exams, setExams] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exam, setExam] = useState({
    title: "",
    description: "",
    passing_score: 60,
    duration_minutes: 30,
    is_pro_only: false,
    badge_label: "",
  });
  const [questions, setQuestions] = useState([blankQuestion()]);

  const load = async () => {
    try {
      setExams((await listExamsAdmin()) || []);
    } catch {
      // ignore
    }
  };
  useEffect(() => {
    load();
  }, []);

  const updateQuestion = (i, patch) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updateOption = (qi, oi, text) =>
    setQuestions((qs) =>
      qs.map((q, idx) =>
        idx === qi
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, text } : o)) }
          : q
      )
    );

  const submit = async (e) => {
    e.preventDefault();
    if (!exam.title.trim()) return toast.error("Title required");
    if (questions.some((q) => !q.question.trim() || q.options.some((o) => !o.text.trim())))
      return toast.error("Fill all questions and options");
    setSaving(true);
    try {
      await createExam({
        ...exam,
        passing_score: Number(exam.passing_score),
        duration_minutes: Number(exam.duration_minutes),
        questions: questions.map((q, i) => ({ ...q, marks: Number(q.marks) || 1, sort_order: i })),
      });
      toast.success("Exam created");
      setShowForm(false);
      setExam({
        title: "",
        description: "",
        passing_score: 60,
        duration_minutes: 30,
        is_pro_only: false,
        badge_label: "",
      });
      setQuestions([blankQuestion()]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Failed to create exam");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this exam?")) return;
    try {
      await deleteExam(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Certification Exams</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-1 bg-[#425F80] text-white rounded-lg px-4 py-2 text-sm"
        >
          <LuPlus size={16} /> {showForm ? "Close" : "New Exam"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-xl border border-gray-100 p-5 mb-8 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              placeholder="Exam title"
              value={exam.title}
              onChange={(e) => setExam({ ...exam, title: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
            <input
              placeholder="Badge label (e.g. Certified Advisor)"
              value={exam.badge_label}
              onChange={(e) => setExam({ ...exam, badge_label: e.target.value })}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <textarea
            placeholder="Description"
            value={exam.description}
            onChange={(e) => setExam({ ...exam, description: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-3 items-center">
            <label className="text-sm text-gray-600">
              Pass %
              <input
                type="number"
                value={exam.passing_score}
                onChange={(e) => setExam({ ...exam, passing_score: e.target.value })}
                className="ml-2 w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm"
              />
            </label>
            <label className="text-sm text-gray-600">
              Duration (min)
              <input
                type="number"
                value={exam.duration_minutes}
                onChange={(e) => setExam({ ...exam, duration_minutes: e.target.value })}
                className="ml-2 w-20 border border-gray-200 rounded-lg px-2 py-1 text-sm"
              />
            </label>
            <label className="text-sm text-gray-600 flex items-center gap-2">
              <input
                type="checkbox"
                checked={exam.is_pro_only}
                onChange={(e) => setExam({ ...exam, is_pro_only: e.target.checked })}
              />
              Pro only
            </label>
          </div>

          <div className="space-y-4">
            {questions.map((q, qi) => (
              <div key={qi} className="border border-gray-100 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Question {qi + 1}</span>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setQuestions((qs) => qs.filter((_, i) => i !== qi))}
                      className="text-red-500"
                    >
                      <LuTrash2 size={16} />
                    </button>
                  )}
                </div>
                <input
                  placeholder="Question text"
                  value={q.question}
                  onChange={(e) => updateQuestion(qi, { question: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2"
                />
                {q.options.map((o, oi) => (
                  <div key={o.key} className="flex items-center gap-2 mb-2">
                    <input
                      type="radio"
                      name={`correct-${qi}`}
                      checked={q.correct_option === o.key}
                      onChange={() => updateQuestion(qi, { correct_option: o.key })}
                    />
                    <span className="uppercase text-xs w-4">{o.key}</span>
                    <input
                      placeholder={`Option ${o.key}`}
                      value={o.text}
                      onChange={(e) => updateOption(qi, oi, e.target.value)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                    />
                  </div>
                ))}
                <p className="text-xs text-gray-400">Select the radio for the correct option.</p>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setQuestions((qs) => [...qs, blankQuestion()])}
              className="text-sm text-[#425F80] flex items-center gap-1"
            >
              <LuPlus size={14} /> Add question
            </button>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="bg-[#425F80] text-white rounded-lg px-6 py-2 text-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Create Exam"}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exams.length === 0 && <p className="text-gray-400">No exams yet.</p>}
        {exams.map((ex) => (
          <div key={ex.id} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex justify-between items-start">
              <h4 className="font-semibold text-gray-800">{ex.title}</h4>
              <button onClick={() => remove(ex.id)} className="text-red-400">
                <LuTrash2 size={16} />
              </button>
            </div>
            <p className="text-sm text-gray-500">{ex.description}</p>
            <p className="text-xs text-gray-400 mt-2">
              Pass {ex.passing_score}% · {ex.duration_minutes} min
              {ex.is_pro_only ? " · PRO" : ""}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CertificationAdmin;
